import React, { useEffect, useState } from 'react';
import './CopyButton.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faClose } from "@fortawesome/pro-solid-svg-icons";
import classNames from 'classnames';
import Tippy from '@tippyjs/react';
import { tippyProps } from '../../../helpers/tippyOptions';


export interface CopyToClipboardProps {
  value: string;
  className?: string;
  successIndicatorDurationMs?: number;
  legacyPresentation?: boolean;
}

const CopyToClipboard: React.FC<CopyToClipboardProps> = (props) => {
  const { className, successIndicatorDurationMs = 3000, value } = props as CopyToClipboardProps;
  const [showSuccessIndicator, setShowSuccessIndicator] = useState(false);
  const [showFailureIndicator, setShowFailureIndicator] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect((): (() => void) => {
    let canceled = false;
    if (showSuccessIndicator) {
      setTimeout((): void => {
        if (canceled) return;
        setShowSuccessIndicator(false);
      }, successIndicatorDurationMs);
    }
    return () => {
      canceled = true;
    };
  }, [showSuccessIndicator, successIndicatorDurationMs]);

  useEffect((): (() => void) => {
    let canceled = false;
    if (showFailureIndicator) {
      setTimeout((): void => {
        if (canceled) return;
        setShowFailureIndicator(false);
      }, successIndicatorDurationMs);
    }
    return () => {
      canceled = true;
    };
  }, [showFailureIndicator, successIndicatorDurationMs]);

  /*
  * Note that copying text does not work on mac when running this locally. It will work once deployed to a server.
  */
  async function copyValueToClipboard(): Promise<void> {
    try {

      await navigator.clipboard.writeText(value);
      setShowSuccessIndicator(true);
    } catch (error) {
      console.error('Failed to copy value to clipboard', error);
      setShowFailureIndicator(true);
    }
  }

  const handleOnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    event.stopPropagation();
    copyValueToClipboard();
  };

  let icon = faCopy;
  if (showSuccessIndicator) icon = faCheck;
  else if (showFailureIndicator) icon = faClose;

  let intent = "none";
  if (showSuccessIndicator) intent = "success";
  else if (showFailureIndicator) intent = "danger";

  let title = 'Copy to clipboard';
  if (showSuccessIndicator) title = 'Copied';
  else if (showFailureIndicator) title = 'Failed to copy';

  return (
    <>
      {!props.legacyPresentation &&
        < button
          {...props}
          className={`copy-button ${className ?? ''} intent-${intent}`}
          title={title}
          type='button'
          onClick={handleOnClick}
        >
          <FontAwesomeIcon icon={icon} />
          {title}
        </button>
      }
      {props.legacyPresentation &&
        <Tippy {...tippyProps} placement="top" content="Copied" visible={showSuccessIndicator && isHovering}>
          <i onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} className={classNames("ce-copyable-icon", "fa", { 'fa-copy': !showSuccessIndicator, 'fa-check-square': showSuccessIndicator })} onClick={handleOnClick} title="Copy to clipboard"></i>
        </Tippy>
      }
    </>
  );
};

export default CopyToClipboard;
